// Central content store for the norse3 site. British English. No invented
// clients, results or statistics. All figures/screens are illustrative.

export const NAV_LINKS = [
  { label: "Case Studies", to: "/case-studies" },
  { label: "Insights", to: "/insights" },
  { label: "FAQs", to: "/faq" },
  { label: "Company", to: "/company" },
];
export const PLATFORM_LINKS = [
  { label: "How It Works", to: "/how-it-works", desc: "The control layer, end to end" },
  { label: "Platform", to: "/platform", desc: "Bifröst, Aegis & Heimdall" },
  { label: "Security & Governance", to: "/security-governance", desc: "Controls, oversight & evidence" },
];
export const SOLUTION_LINKS = [
  { label: "Financial Services", to: "/financial-services", desc: "Control AI & customer conversations at scale" },
  { label: "Defence & National Security", to: "/defence", desc: "Independent governance around the AI you deploy" },
  { label: "Enterprise AI", to: "/enterprise-ai", desc: "Runtime control for employee AI, copilots & agents" },
  { label: "Regulated Industries", to: "/regulated-industries", desc: "Policy, oversight & evidence for regulated sectors" },
];
export const PRODUCT_LINKS = [
  { label: "V\u00f6r", to: "/vor", desc: "Governed AI chat widget for your site or app" },
  { label: "L\u00f6gberg", to: "/logberg", desc: "Screen regulated conversations for review" },
  { label: "Skjold", to: "/skjold", desc: "Local document guard for ChatGPT, Claude & more" },
];

export const TOOL_LINKS = [
  { label: "Interactive Demo", to: "/interactive-demo", desc: "See a live interaction become evidence" },
  { label: "Governance Tools", to: "/governance-tools", desc: "Assess readiness, policy, deployment" },
  { label: "ROI & Value Calculator", to: "/roi-calculator", desc: "Model hours saved and coverage gained" },
];

export const SIGNAL_STEPS = [
  { key: "user", label: "User", sub: "Person or system", tone: "neutral" },
  { key: "channel", label: "AI / channel", sub: "Chatbot, copilot, agent", tone: "neutral" },
  { key: "relay", label: "Bifr\u00f6st Relay", sub: "Connect \u00b7 normalise \u00b7 route", tone: "brand" },
  { key: "engine", label: "Aegis Engine", sub: "Identify \u00b7 decide", tone: "brand" },
  { key: "action", label: "Allow / Warn / Block / Redact / Escalate", sub: "Configured action", tone: "action" },
  { key: "console", label: "Heimdall Console", sub: "Evidence \u00b7 human review", tone: "brand" },
];

export const LIFECYCLE = [
  { step: "Identify", desc: "Jailbreaks, PII exposure, manipulation, unauthorised advice, harmful content and policy breaches." },
  { step: "Decide", desc: "Evaluate against your configured policies, roles, jurisdiction and risk appetite." },
  { step: "Act", desc: "Allow, warn, block, redact or escalate \u2014 as your policy defines." },
  { step: "Evidence", desc: "Record the event, the policy applied, the action taken and the timestamp." },
  { step: "Review", desc: "Triage in a human-controlled console. Your firm remains accountable." },
];

export const NATIVE_COMPARISON = {
  columns: ["Native provider controls", "norse3 control layer"],
  rows: [
    { label: "Reflects your firm-specific policy", native: "partial", norse3: "yes" },
    { label: "Aware of your roles and permissions", native: "no", norse3: "yes" },
    { label: "Applies your jurisdiction and risk appetite", native: "no", norse3: "yes" },
    { label: "Creates evidence for your review duties", native: "partial", norse3: "yes" },
    { label: "Consistent across a multi-model estate", native: "no", norse3: "yes" },
    { label: "Independent of the model and vendor", native: "no", norse3: "yes" },
    { label: "Human-controlled escalation and sign-off", native: "partial", norse3: "yes" },
  ],
};

export const SCENARIOS = [
  {
    id: "jailbreak",
    tab: "Jailbreak input",
    tone: "block",
    event: "A user submits a prompt engineered to override system instructions and extract restricted behaviour.",
    detection: "Aegis Engine matches the input against jailbreak and manipulation signatures and scores it high-risk.",
    action: "The request is blocked before it reaches the downstream model. The user receives a safe fallback response.",
    outcome: "No restricted behaviour is triggered. A high-severity event is written to Heimdall for review.",
    direction: "Inbound prompt",
  },
  {
    id: "advice",
    tab: "Unauthorised advice",
    tone: "escalate",
    event: "An AI response drifts towards specific investment advice a firm is not authorised to give in this context.",
    detection: "Aegis Engine inspects the outbound response and flags a potential unauthorised-advice breach against policy.",
    action: "The response is intercepted before release and escalated to a named reviewer in Heimdall.",
    outcome: "A human decides how to proceed. The interception, policy and decision are all evidenced.",
    direction: "Outbound response",
  },
  {
    id: "pii",
    tab: "PII exposure",
    tone: "redact",
    event: "A message contains a customer identifier \u2014 for example a passport or bank detail \u2014 that should not be exposed.",
    detection: "Aegis Engine performs entity recognition and identifies personal data against your data policy.",
    action: "The sensitive fields are redacted and replaced with secure tokens before the message continues.",
    outcome: "The conversation proceeds safely. A time-stamped record is retained for the compliance team.",
    direction: "In-flight message",
  },
  {
    id: "indirect",
    tab: "Indirect injection (file)",
    tone: "block",
    event: "A copilot reads a document that contains hidden instructions telling it to export data.",
    detection: "The file content and the proposed tool action are inspected separately; the hidden instruction is flagged.",
    action: "The export is blocked and the session is held for review before any action can complete.",
    outcome: "Untrusted document text cannot be turned into an unauthorised side effect. The event is recorded.",
    direction: "Copilot + file",
  },
  {
    id: "agent",
    tab: "Agent tool call",
    tone: "escalate",
    event: "An AI agent proposes a privileged action — for example writing to production or sending a file externally.",
    detection: "The tool intent is evaluated against your tool, role and action policy before execution.",
    action: "The action is held for approval and routed to an authorised person in Heimdall.",
    outcome: "A high-impact action does not occur solely because the model requested it. The decision is evidenced.",
    direction: "Agent tool intent",
  },
];

export const POLICY_OUTCOMES = [
  { name: "Allow", desc: "Continue unchanged when the interaction is within policy.", tone: "green" },
  { name: "Warn", desc: "Continue but attach a visible or operational warning.", tone: "green" },
  { name: "Rewrite", desc: "Produce a policy-constrained alternative where configured.", tone: "cyan" },
  { name: "Redact", desc: "Mask or remove defined sensitive fields before delivery.", tone: "cyan" },
  { name: "Block", desc: "Prevent delivery or execution of a clear risk.", tone: "red" },
  { name: "Hold", desc: "Pause pending authorised human review.", tone: "amber" },
  { name: "Quarantine", desc: "Restrict a session after repeated or severe signals.", tone: "amber" },
  { name: "Escalate", desc: "Route to a named reviewer for a human decision.", tone: "amber" },
];

export const RISK_FAMILIES = [
  {
    id: "prompt",
    name: "Prompt & instruction attack",
    examples: "Direct jailbreak, indirect prompt injection, system-prompt extraction.",
    control: "Block, sanitise or isolate content, limit tool authority and alert.",
    human: "Security reviews high-severity attempts and adjusts policy.",
    evidence: "Rule matched, excerpt, action and time recorded in Heimdall.",
  },
  {
    id: "data",
    name: "Sensitive data",
    examples: "Personal data, credentials, confidential documents and secrets.",
    control: "Redact, deny destination or hold, logging a minimal reference.",
    human: "Compliance confirms handling against the data policy.",
    evidence: "Minimal evidence reference retained — not the raw data.",
  },
  {
    id: "conduct",
    name: "Conduct & advice",
    examples: "Unauthorised recommendation, misleading assurance, missing disclosure.",
    control: "Block or rewrite, then route to a human reviewer.",
    human: "A reviewer assesses the customer-outcome implications.",
    evidence: "Policy, decision and rationale linked for assurance.",
  },
  {
    id: "safety",
    name: "Safety & vulnerability",
    examples: "Harmful advice, vulnerability signals or unsafe instructions.",
    control: "Safer wording, specialist escalation, restricted automation.",
    human: "A trained person reviews the original context.",
    evidence: "Escalation and handling steps are time-stamped.",
  },
  {
    id: "authority",
    name: "Agent authority",
    examples: "Unauthorised tool use, write action, export or privilege escalation.",
    control: "Allowlist, approval, rate budget or session stop.",
    human: "An owner approves or rejects the proposed action.",
    evidence: "System, policy, reason and action captured.",
  },
  {
    id: "reliability",
    name: "Quality & reliability",
    examples: "Low-confidence transcription, unsupported answer or contradiction.",
    control: "Mark uncertainty, request information or send to review.",
    human: "A reviewer resolves the ambiguity rather than a score hiding it.",
    evidence: "Confidence and uncertainty surfaced, not concealed.",
  },
];

export const CONVERSATION_SURFACES = [
  { title: "Customer chatbots", desc: "Front-line assistants answering regulated customer questions." },
  { title: "Internal copilots", desc: "Staff-facing assistants embedded in day-to-day tools." },
  { title: "Support tooling", desc: "Help-desk and ticketing conversations across agents and AI." },
  { title: "CRM workflows", desc: "AI-assisted actions inside customer-relationship systems." },
  { title: "Human-led conversations", desc: "Calls and messages handled by people, not models." },
];

export const PLATFORM_COMPONENTS = [
  {
    name: "Bifr\u00f6st Relay",
    role: "The connection layer",
    desc: "Connects approved systems, normalises message formats, encrypts in transit and routes conversations to the engine. Designed to minimise unnecessary data movement.",
    points: ["Connect approved systems and channels", "Normalise and encrypt messages", "Route to the engine and back", "Deployment options to keep data close"],
  },
  {
    name: "Aegis Engine",
    role: "The decision layer",
    desc: "Detects and classifies risk, evaluates each interaction against your configured policies and applies the action you have defined \u2014 allow, warn, block, redact or escalate.",
    points: ["Detect and classify risk signals", "Evaluate against your policies", "Apply the configured action", "Score severity for triage"],
  },
  {
    name: "Heimdall Console",
    role: "The oversight layer",
    desc: "Gives risk, security and compliance teams a single place to triage alerts, review incidents, manage policies and produce reporting \u2014 with humans in control of decisions.",
    points: ["Alert triage and incident review", "Policy management and versioning", "Reviewable evidence trail", "Reporting and management information"],
  },
];

export const DEPLOYMENT_OPTIONS = [
  { name: "Hosted", desc: "Run by norse3 as a managed service, subject to your validation and data requirements." },
  { name: "Private cloud", desc: "Deployed within your own cloud tenancy for tighter data-residency control." },
  { name: "Customer-controlled", desc: "Operated inside your controlled environment where regulation requires it." },
];

export const ONBOARDING_STEPS = [
  { step: "Discovery", desc: "Understand your conversations, systems, risks and obligations." },
  { step: "Policy mapping", desc: "Translate your policies into configurable, testable controls." },
  { step: "Integration", desc: "Connect approved channels through Bifr\u00f6st Relay." },
  { step: "Testing", desc: "Validate detections and actions against agreed scenarios." },
  { step: "Launch", desc: "Go live with the controls and reviewers you have signed off." },
  { step: "Monitoring", desc: "Review evidence, refine policies and report on an ongoing basis." },
];

export const PLATFORM_FAQS = [
  { q: "Is norse3 an AI model?", a: "norse3 can use narrow models for classification, but its value is the control system around them: rules, policy decisions, enforcement, human review and evidence. It does not replace your model or application." },
  { q: "Who remains accountable for decisions?", a: "Your firm does. norse3 supports alignment and is designed to reduce risk, but people make and own the final decisions in the Heimdall Console." },
  { q: "Whose policies are applied?", a: "Yours. Policies are configured to your jurisdiction, roles, risk appetite and approval thresholds. norse3 provides the mechanism to implement and evidence those decisions — it does not decide your policy for you." },
  { q: "What can the system do when it finds risk?", a: "Depending on policy and integration, it can allow, warn, rewrite, redact, block, hold, quarantine, alert or route to a human reviewer." },
  { q: "How does escalation work?", a: "When an interaction meets an escalation rule, it is routed to a named reviewer with the event, policy and context attached for a human decision. High-impact overrides are attributable and logged." },
  { q: "What evidence is created?", a: "For each handled interaction, norse3 records the event, the policy applied, the action taken, the reason and the timestamp — retained per your settings — distinguishing platform-observed facts from judgement only your firm can provide." },
  { q: "Where does data live?", a: "It depends on the deployment option you choose — hosted, private cloud or customer-controlled — subject to validation of your requirements. We describe the actual data path rather than using one universal privacy claim." },
  { q: "Does norse3 guarantee compliance?", a: "No. It supports compliance and oversight, but your organisation retains accountability and must apply its own legal and regulatory judgement." },
  { q: "Does it work across multiple models?", a: "Yes. norse3 is model-agnostic and applies one consistent control and evidence layer across a multi-model estate, subject to the available integration." },
  { q: "What happens if norse3 is unavailable?", a: "Each workflow has an agreed fail-open, fail-closed or fallback design, together with monitoring, timeouts, queues and manual procedures." },
];

export const LOGBERG_FLOW = [
  { step: "Connect", desc: "Bring in regulated calls and transcripts from approved sources." },
  { step: "Screen", desc: "Screen interactions against your policies and risk indicators." },
  { step: "Rank", desc: "Prioritise material issues so reviewers focus where it matters." },
  { step: "Route", desc: "Send cases to the right reviewer with full context attached." },
  { step: "Capture", desc: "Record reviewer decisions and link the supporting evidence." },
  { step: "Report", desc: "Produce management information for oversight and assurance." },
];

export const FS_WORKFLOWS = [
  {
    title: "Regulated customer conversations",
    risk: "A customer-facing assistant drifts towards advice or guidance beyond what the firm is authorised to give.",
    intervention: "The response is intercepted before release and, where policy requires, escalated for a human decision.",
    human: "A reviewer sees the interaction, the policy applied and the recommended action in one place.",
    evidence: "The event, policy, action and decision are retained as a linked, time-stamped record.",
  },
  {
    title: "Internal AI / copilot oversight",
    risk: "Staff-facing copilots surface sensitive data or produce outputs that breach internal policy.",
    intervention: "Sensitive fields are redacted and out-of-policy outputs are warned, blocked or escalated.",
    human: "Risk owners monitor copilot activity and review flagged interactions across teams.",
    evidence: "A consistent trail across copilots supports internal audit and assurance.",
  },
  {
    title: "QA & Consumer Duty evidence",
    risk: "QA and compliance teams struggle to evidence fair outcomes across large conversation volumes.",
    intervention: "Interactions are screened and material issues are ranked for reviewer attention.",
    human: "Reviewers capture decisions and rationale against a structured, repeatable process.",
    evidence: "Management information supports Consumer Duty and QA reporting to oversight bodies.",
  },
];

export const SECURITY_ITEMS = [
  { title: "Data flow", desc: "Messages travel through Bifr\u00f6st Relay to the engine and back, following the paths you approve." },
  { title: "Data minimisation", desc: "Deployment options are designed to move only what is needed for the control to work." },
  { title: "Encryption", desc: "Messages are encrypted in transit between connected systems and norse3 components." },
  { title: "Retention", desc: "Retention windows for events and evidence are configurable to your policy." },
  { title: "Access", desc: "Access to the console and records is role-based and configurable by your administrators." },
  { title: "Tenant separation", desc: "Customer conversations, personal data and incidents are not commingled across tenants by default." },
  { title: "Audit logs", desc: "Actions in the console are logged to support internal review and assurance." },
  { title: "Human oversight", desc: "Escalation and sign-off stay with your people; norse3 does not make final decisions for you." },
];

export const TEAM = [
  { name: "Alastair Band", role: "Co-Founder \u00b7 CEO & Product", initials: "AB", linkedin: "https://www.linkedin.com/in/alastairband/" },
  { name: "Lawrence Hui", role: "Co-Founder \u00b7 CTO", initials: "LH", linkedin: "https://www.linkedin.com/in/ka-chun-hui/" },
  { name: "Thomas Pritchard", role: "Co-Founder \u00b7 CRO", initials: "TP", linkedin: "https://www.linkedin.com/in/thomas-pritchard-chcp2222/" },
  { name: "Michael Simmonds", role: "Co-Founder \u00b7 COO", initials: "MS", linkedin: "https://www.linkedin.com/in/michaelsimmonds/" },
];

export const FOUNDER_BIOS = {
  "AB": "Alastair Band is Co-Founder and CEO of Norse3, leading the company’s strategy and product direction. His background spans emerging technology, marketing, partnerships and community development, including serving as Head of Community at Boson Protocol. At Norse3, his focus is on helping organisations adopt AI with clearer accountability, practical controls and evidence of how their systems behave.",
  "LH": "Lawrence Hui is Co-Founder and CTO of Norse3, bringing practical experience in production AI systems, voice agents and AI safeguards. His engineering work includes infrastructure that separates credentials from AI models and enables people to review proposed actions before execution. This background supports Norse3’s focus on controlling AI interactions, protecting sensitive information and maintaining human oversight.",
  "TP": "Thomas Pritchard is Co-Founder and CRO of Norse3 and a Director of Charter HCP, with 15 years of experience across corporate finance, private equity, venture capital and debt structuring. His international experience includes complex funding transactions, financial analysis and commercial negotiations. He brings a financial services perspective to Norse3, alongside a strong interest in applying AI and connected systems to due diligence, analysis and client delivery.",
  "MS": "Michael Simmonds is Co-Founder and COO of Norse3, with a background in banking, finance and debt structures. His work for Norse3 has included drafting investment agreements and investor documentation, alongside securing HMRC Advance Assurance under the SEIS/EIS regime. He brings financial and transaction experience to the company’s operations and the development of its corporate and investment foundations."
};

export const INSIGHTS = [
  {
    slug: "social-engineering-and-prompt-injection",
    title: "Social engineering and prompt injection: a runtime problem",
    category: "Threats",
    readTime: "7 min read",
    excerpt: "Prompt injection and social engineering against AI systems are not theoretical. Here is how the manipulation works, why it matters for regulated firms, and what runtime controls actually change.",
    body: [
      "Prompt injection is the practice of crafting inputs that push a model to ignore its instructions or reveal behaviour it should not. Social engineering applies the same intent to the people and processes around a model. Both target the live conversation rather than the code, which is why they slip past static reviews.",
      "For regulated firms the risk is concrete: an assistant persuaded to give advice it should not, a support agent nudged into disclosing data, or a copilot coaxed into acting outside policy. The damage is not only technical \u2014 it is regulatory, reputational and operational.",
      "Runtime controls change the picture because they inspect interactions as they happen. An independent layer can identify manipulation, decide against firm-specific policy, act by blocking or escalating, and evidence what occurred. Crucially, a person stays accountable for the outcome.",
      "norse3 is designed to sit in exactly this position \u2014 model-agnostic, independent of the vendor, and focused on the conversational layer where these attacks land.",
    ],
  },
  {
    slug: "the-agent-oversight-gap",
    title: "The oversight gap at the heart of agentic AI",
    category: "Governance",
    readTime: "6 min read",
    excerpt: "AI agents promise real productivity gains, but there is an unresolved oversight gap in agentic systems. What it is, why it matters, and how independent runtime controls help.",
    body: [
      "Agentic AI chains steps together \u2014 reading, deciding and acting with limited human involvement between actions. That autonomy is the point, and also the problem: the more an agent does on its own, the harder it is to see, control and evidence each step.",
      "Native guardrails help, but they optimise for capability and speed and cannot interpret your firm's obligations. They rarely reflect your roles, your jurisdiction or your evidence duties, and they are inconsistent across a multi-model estate.",
      "An independent control layer addresses the gap by applying your policies at runtime, intervening when required and creating a reviewable trail. It does not replace your compliance function \u2014 it gives that function something to see and act on.",
      "This is the role norse3 is built for: an oversight layer above the model, the cloud and the chatbot, keeping humans in control of the decisions that matter.",
    ],
  },
];

export const COMPANY_VALUES = [
  { title: "Independent by design", desc: "We sit outside the model and the vendor, so the evidence we create is objective." },
  { title: "Humans stay accountable", desc: "We support alignment and are designed to reduce risk. People make the final decisions." },
  { title: "Evidence over assertion", desc: "Every handled interaction leaves a reviewable record, not a claim." },
  { title: "Regulated-first", desc: "We build for the obligations UK financial services teams actually carry." },
];

export const ENQUIRY_TYPES = [
  "Book a walkthrough",
  "Discuss a L\u00f6gberg pilot",
  "Partner enquiry",
  "General enquiry",
];
